// ── FIREBASE SETUP ───────────────────────────────────────────
import { initializeApp } from 'firebase/app';
import {
  getFirestore,
  doc, collection,
  getDoc, getDocs,
  setDoc, addDoc, deleteDoc, updateDoc,
  onSnapshot,
  serverTimestamp,
  query, orderBy, limit,
  writeBatch,
  deleteField,
} from 'firebase/firestore';

const FIREBASE_CONFIG = {
  apiKey:            "AIzaSyBuvHRnPb6Vlh22VaPParGHKJOYNx7Tnpg",
  authDomain:        "pxtracker-d6acd.firebaseapp.com",
  projectId:         "pxtracker-d6acd",
  storageBucket:     "pxtracker-d6acd.firebasestorage.app",
  messagingSenderId: "368405501802",
  appId:             "1:368405501802:web:70c689e6bb000515173713",
};

export const isFirebaseConfigured = () =>
  Object.values(FIREBASE_CONFIG).every(v => v !== 'REPLACE_ME');

let app;
export let db;
if (isFirebaseConfigured()) {
  app = initializeApp(FIREBASE_CONFIG);
  db  = getFirestore(app);
}

// ── REFS ──────────────────────────────────────────────────────
export const getShiftRef    = (code) => doc(db, 'shifts', code.toUpperCase().trim());
export const getPatientsCol = (code) => collection(db, 'shifts', code.toUpperCase().trim(), 'patients');
export const getPatientRef  = (code, id) => doc(db, 'shifts', code.toUpperCase().trim(), 'patients', id);

// ── SHIFT META (teams only) ───────────────────────────────────
export const loadShiftMeta = async (code) => {
  if (!isFirebaseConfigured()) return null;
  try {
    const snap = await getDoc(getShiftRef(code));
    return snap.exists() ? snap.data() : null;
  } catch (e) { console.error('loadShiftMeta:', e); return null; }
};

export const saveShiftMeta = async (code, teams, subspecs) => {
  if (!isFirebaseConfigured()) return;
  const payload = { teams, updatedAt: serverTimestamp() };
  if (subspecs !== undefined) payload.subspecs = subspecs;
  await setDoc(getShiftRef(code), payload, { merge: true });
};

// ── PATIENTS SUBCOLLECTION ────────────────────────────────────

export const loadPatients = async (code) => {
  if (!isFirebaseConfigured()) return null;
  try {
    const snap = await getDocs(getPatientsCol(code));
    if (snap.empty) return null;
    return snap.docs.map(d => d.data());
  } catch (e) { console.error('loadPatients:', e); return null; }
};

export const savePatient = async (code, patient) => {
  if (!isFirebaseConfigured()) return;
  await setDoc(getPatientRef(code, patient.id), patient);
};

export const removePatient = async (code, patientId) => {
  if (!isFirebaseConfigured()) return;
  await deleteDoc(getPatientRef(code, patientId));
};

export const mergeIntoShift = async (targetCode, { patients = [], teams = [], subspecs = [] } = {}) => {
  if (!isFirebaseConfigured()) return;
  const batch = writeBatch(db);
  for (const p of patients) {
    const newId = `${p.id}_imp_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
    batch.set(getPatientRef(targetCode, newId), { ...p, id: newId });
  }
  await batch.commit();
  const existingMeta = await loadShiftMeta(targetCode);
  const existingTeams    = existingMeta?.teams    || [];
  const existingSubspecs = existingMeta?.subspecs || [];
  const mergedTeams    = [...existingTeams,    ...teams.filter(t  => !existingTeams.find(e    => e.id === t.id))];
  const mergedSubspecs = [...existingSubspecs, ...subspecs.filter(s => !existingSubspecs.find(e => e.id === s.id))];
  await saveShiftMeta(targetCode, mergedTeams, mergedSubspecs);
};

export const savePatientsBatch = async (code, patients) => {
  if (!isFirebaseConfigured() || !patients.length) return;
  const batch = writeBatch(db);
  for (const p of patients) batch.set(getPatientRef(code, p.id), p);
  await batch.commit();
};

export const subscribePatients = (code, onPatientChange, onError) => {
  if (!isFirebaseConfigured()) return () => {};
  return onSnapshot(
    getPatientsCol(code),
    (snap) => {
      snap.docChanges().forEach(change => {
        onPatientChange(change.type, change.doc.data());
      });
    },
    onError || (() => {})
  );
};

// ── MIGRATION: old flat-array format → subcollection ─────────
export const migrateIfNeeded = async (code) => {
  if (!isFirebaseConfigured()) return;
  try {
    const shiftSnap = await getDoc(getShiftRef(code));
    if (!shiftSnap.exists()) return;
    const data = shiftSnap.data();
    if (!data.patients || !Array.isArray(data.patients) || !data.patients.length) return;

    const existing = await getDocs(getPatientsCol(code));
    if (!existing.empty) {
      await updateDoc(getShiftRef(code), { patients: deleteField() });
      return;
    }

    const batch = writeBatch(db);
    for (const p of data.patients) {
      if (p.id) batch.set(getPatientRef(code, p.id), p);
    }
    batch.update(getShiftRef(code), { patients: deleteField() });
    await batch.commit();
    console.log(`Migrated ${data.patients.length} patients for shift ${code}`);
  } catch (e) {
    console.error('migrateIfNeeded error:', e);
  }
};

// ── SHIFT CREATION ────────────────────────────────────────────
export const createShift = async (code, _patients, teams, pin) => {
  if (!isFirebaseConfigured()) return;
  const payload = {
    teams,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };
  if (pin) payload.pinHash = btoa(pin);
  await setDoc(getShiftRef(code), payload);
};

// ── LEGACY COMPAT ─────────────────────────────────────────────
export const loadShift = async (code) => {
  if (!isFirebaseConfigured()) return null;
  try {
    const [meta, patients] = await Promise.all([loadShiftMeta(code), loadPatients(code)]);
    if (!meta && !patients) return null;
    return { patients: patients || [], teams: meta?.teams || [], meta: meta?.meta || {}, pinHash: meta?.pinHash || null, subspecs: meta?.subspecs || [] };
  } catch (e) { console.error('loadShift:', e); return null; }
};

export const saveShift = async (code, patients, teams) => {
  if (!isFirebaseConfigured()) return;
  await Promise.all([saveShiftMeta(code, teams), savePatientsBatch(code, patients)]);
};

export const subscribeShift = (_code, _onData, _onError) => () => {};

// ── MESSAGING ─────────────────────────────────────────────────
export const sendMessage = async (code, patientId, text, author) => {
  if (!isFirebaseConfigured()) return;
  const colRef = collection(db, 'shifts', code.toUpperCase(), 'messages_' + patientId);
  await addDoc(colRef, { text, author, ts: serverTimestamp(), tsNum: Date.now() });
};

export const subscribeMessages = (code, patientId, onData) => {
  if (!isFirebaseConfigured()) return () => {};
  const colRef = collection(db, 'shifts', code.toUpperCase(), 'messages_' + patientId);
  const q = query(colRef, orderBy('tsNum', 'asc'));
  return onSnapshot(q, snap => {
    onData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

export const deleteMessage = async (code, patientId, msgId) => {
  if (!isFirebaseConfigured()) return;
  await deleteDoc(doc(db, 'shifts', code.toUpperCase(), 'messages_' + patientId, msgId));
};

export const deleteAllMessages = async (code, patientId) => {
  if (!isFirebaseConfigured()) return;
  const colRef = collection(db, 'shifts', code.toUpperCase(), 'messages_' + patientId);
  const snap = await getDocs(colRef);
  if (snap.empty) return;
  const batch = writeBatch(db);
  snap.docs.forEach(d => batch.delete(d.ref));
  await batch.commit();
};

// ── AUDIT LOG ─────────────────────────────────────────────────
export const logAudit = async (code, entry) => {
  if (!isFirebaseConfigured()) return;
  try {
    const colRef = collection(db, 'shifts', code.toUpperCase(), 'audit');
    await addDoc(colRef, { ...entry, ts: serverTimestamp(), tsNum: Date.now() });
  } catch { /* silent */ }
};

export const subscribeAudit = (code, onData) => {
  if (!isFirebaseConfigured()) return () => {};
  const colRef = collection(db, 'shifts', code.toUpperCase(), 'audit');
  const q = query(colRef, orderBy('tsNum', 'desc'), limit(100));
  return onSnapshot(q, snap => {
    onData(snap.docs.map(d => ({ id: d.id, ...d.data() })));
  });
};

// ── PUSH NOTIFICATIONS ────────────────────────────────────────
export const saveFCMToken = async (code, token, userName) => {
  if (!isFirebaseConfigured()) return;
  const ref = doc(db, 'shifts', code.toUpperCase(), 'tokens', token.slice(-20));
  await setDoc(ref, { token, userName, ts: serverTimestamp() }, { merge: true });
};

export const getShiftTokens = async (code) => {
  if (!isFirebaseConfigured()) return [];
  try {
    const col = collection(db, 'shifts', code.toUpperCase(), 'tokens');
    const snap = await getDocs(col);
    return snap.docs.map(d => d.data().token);
  } catch { return []; }
};
